// src/admin/pages/AdminEmailTemplates.js
import React, { useState, useEffect, useContext } from "react";
import { FaTags, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { AdminLanguageContext } from "../context/AdminLanguageContext";

import {
  getAdminCoupons,
  createAdminCoupon,
  updateAdminCoupon,
  deleteAdminCoupon,
  activateAllCoupons,
  deactivateAllCoupons,
} from "../../services/adminCoupons.api";

function AdminEmailTemplates() {
  const navigate = useNavigate();
  const { lang } = useContext(AdminLanguageContext);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); 
      // "activate" | "deactivate" | null
  const [confirmDelete, setConfirmDelete] = useState(null); 
     // will hold coupon object or null

  const [form, setForm] = useState({
    code: "",
    discount_type: "percent",
    discount_value: "",
    max_uses: "",
    is_active: true,
  });

  const TEXT = {
    EN: {
      title: "Promo Codes",
      add: "Add Promo Code",
      code: "Code",
      type: "Type",
      value: "Value",
      maxUses: "Max Uses",
      used: "Used",
      status: "Status",
      actions: "Actions",
      active: "Active",
      inactive: "Inactive",
      save: "Save",
      cancel: "Cancel",
      loading: "Loading promo codes...",
      empty: "No promo codes found.",
    },
    AR: {
      title: "أكواد الخصم",
      add: "إضافة كود خصم",
      code: "الكود",
      type: "النوع",
      value: "القيمة",
      maxUses: "الحد الأقصى",
      used: "مستخدم",
      status: "الحالة",
      actions: "إجراءات",
      active: "مفعل",
      inactive: "غير مفعل",
      save: "حفظ",
      cancel: "إلغاء",
      loading: "جاري تحميل أكواد الخصم...",
      empty: "لا توجد أكواد خصم.",
    },
  }[lang];

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
const perms = JSON.parse(localStorage.getItem("staffPermissions") || {});
const isAdmin = adminUser.role === "admin";

const canAccess = isAdmin || perms.can_manage_marketing;


  useEffect(() => {
    async function load() {
      setLoading(true);
      const list = await getAdminCoupons();
      setCoupons(list);
      setLoading(false);
    }
    load();
  }, []);

  const resetForm = () => {
    setForm({
      code: "",
      discount_type: "percent",
      discount_value: "",
      max_uses: "",
      is_active: true,
    });
    setEditing(null);
  };

  const saveCoupon = async () => {
    if (!form.code || !form.discount_value) return;

    if (editing) {
      await updateAdminCoupon(editing.id, form);
    } else {
      await createAdminCoupon(form);
    }

    const list = await getAdminCoupons();
    setCoupons(list);
    resetForm();
  };

  return (
    <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className="flex-1 lg:ml-64 transition-all duration-300">
        <AdminHeader setIsOpen={setIsOpen} />

        <main className="p-4 sm:p-6 animate-fadeSlide">
  {!canAccess && (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-700 font-semibold">
        {lang === "AR"
          ? "ليس لديك صلاحية لإدارة أكواد الخصم"
          : "You do not have permission to manage promo codes."}
      </p>
    </div>
  )}

  {canAccess && (
    <>


          {/* TITLE */}
          <div className="flex flex-wrap gap-3 items-center mb-6">
  <h1 className="text-2xl font-bold text-cyan-700 flex items-center gap-2">
    <FaTags /> {TEXT.title}
  </h1>

  <div className="flex gap-2 ml-auto">
    <button
      className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
      onClick={() => setConfirmAction("activate")}
    >
      {lang === "EN" ? "Activate All" : "تفعيل الكل"}
    </button>

    <button
      className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
      onClick={() => setConfirmAction("deactivate")}
    >
      {lang === "EN" ? "Deactivate All" : "إيقاف الكل"}
    </button>

    <button
      className="bg-cyan-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-cyan-700"
      onClick={() => navigate("/admin/email-templates/new")}
    >
      <FaPlus /> {TEXT.add}
    </button>
  </div>
</div>


          {/* FORM */}
          <div className="bg-white rounded-xl shadow p-4 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3">
            <input
              placeholder={TEXT.code}
              className="border p-2 rounded"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />

            <select
              className="border p-2 rounded"
              value={form.discount_type}
              onChange={(e) =>
                setForm({ ...form, discount_type: e.target.value })
              }
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed</option>
            </select>

            <input
              type="number"
              placeholder={TEXT.value}
              className="border p-2 rounded"
              value={form.discount_value}
              onChange={(e) =>
                setForm({ ...form, discount_value: e.target.value })
              }
            />

            <input
              type="number"
              placeholder={TEXT.maxUses}
              className="border p-2 rounded"
              value={form.max_uses}
              onChange={(e) =>
                setForm({ ...form, max_uses: e.target.value })
              }
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
              />
              <span>{TEXT.active}</span>
            </div>

            <button
              className="md:col-span-5 bg-cyan-600 text-white py-2 rounded hover:bg-cyan-700"
              onClick={saveCoupon}
            >
              {TEXT.save}
            </button>
          </div>

          {/* DESKTOP TABLE */}
          <div className="hidden md:block bg-white rounded-xl shadow overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-3 text-center">{TEXT.code}</th>
                  <th className="p-3 text-center">{TEXT.type}</th>
                  <th className="p-3 text-center">{TEXT.value}</th>
                  <th className="p-3 text-center">{TEXT.maxUses}</th>
                  <th className="p-3 text-center">{TEXT.used}</th>
                  <th className="p-3 text-center">{TEXT.status}</th>
                  <th className="p-3 text-center">{TEXT.actions}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="p-4 text-center">
                      {TEXT.loading}
                    </td>
                  </tr>
                ) : coupons.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-4 text-center">
                      {TEXT.empty}
                    </td>
                  </tr>
                ) : (
                  coupons.map((c) => (
                    <tr key={c.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-semibold text-center">{c.code}</td>
                      <td className="p-3 text-center">{c.discount_type}</td>
                      <td className="p-3 text-center">{c.discount_value}</td>
                      <td className="p-3 text-center">{c.max_uses ?? "∞"}</td>
                      <td className="p-3 text-center">{c.used_orders}</td>
                      <td className="p-3 text-center">
                        {c.is_active ? TEXT.active : TEXT.inactive}
                      </td>
                      <td className="p-3 flex gap-3 justify-center">
                        <button
                          onClick={() => {
                            setEditing(c);
                            setForm({
                              code: c.code,
                              discount_type: c.discount_type,
                              discount_value: c.discount_value,
                              max_uses: c.max_uses ?? "",
                              is_active: !!c.is_active,
                            });
                          }}
                        >
                          <FaEdit />
                        </button>

                        <button
  className="text-red-600"
  onClick={() => setConfirmDelete(c)}
>
  <FaTrash />
</button>

                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS */}
          <div className="md:hidden space-y-4">
            {coupons.map((c) => (
              <div key={c.id} className="bg-white p-4 rounded-xl shadow">
                <p className="font-bold text-lg">{c.code}</p>
                <p>{TEXT.type}: {c.discount_type}</p>
                <p>{TEXT.value}: {c.discount_value}</p>
                <p>{TEXT.used}: {c.used_orders}</p>
                <p>{TEXT.status}: {c.is_active ? TEXT.active : TEXT.inactive}</p>
              </div>
            ))}
          </div>
        {confirmAction && (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
    <div className="bg-white w-80 rounded-xl p-6 shadow-xl animate-fadeSlide">
      <h3 className="text-lg font-bold mb-3">
        {confirmAction === "activate"
          ? lang === "EN"
            ? "Activate all coupons?"
            : "تفعيل جميع الكوبونات؟"
          : lang === "EN"
          ? "Deactivate all coupons?"
          : "إيقاف جميع الكوبونات؟"}
      </h3>

      <p className="text-sm text-gray-600 mb-6">
        {lang === "EN"
          ? "This action will affect all promo codes."
          : "سيتم تطبيق هذا الإجراء على جميع أكواد الخصم."}
      </p>

      <div className="flex justify-end gap-3">
        <button
          className="px-4 py-2 bg-gray-200 rounded-lg"
          onClick={() => setConfirmAction(null)}
        >
          {TEXT.cancel}
        </button>

        <button
          className={`px-4 py-2 rounded-lg text-white ${
            confirmAction === "activate"
              ? "bg-green-600 hover:bg-green-700"
              : "bg-red-600 hover:bg-red-700"
          }`}
          onClick={async () => {
            if (confirmAction === "activate") {
              await activateAllCoupons();
              toast.success(
                lang === "EN"
                  ? "All coupons activated"
                  : "تم تفعيل جميع الكوبونات"
              );
            } else {
              await deactivateAllCoupons();
              toast.success(
                lang === "EN"
                  ? "All coupons deactivated"
                  : "تم إيقاف جميع الكوبونات"
              );
            }

            const list = await getAdminCoupons();
            setCoupons(list);
            setConfirmAction(null);
          }}
        >
          {TEXT.save}
        </button>
      </div>
    </div>
  </div>
)}

{confirmDelete && (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
    <div className="bg-white w-80 rounded-xl p-6 shadow-xl animate-fadeSlide">
      <h3 className="text-lg font-bold mb-3 text-red-600">
        {lang === "EN" ? "Delete promo code?" : "حذف كود الخصم؟"}
      </h3>

      <p className="text-sm text-gray-600 mb-6">
        {lang === "EN"
          ? `Are you sure you want to delete "${confirmDelete.code}"? This action cannot be undone.`
          : `هل أنت متأكد من حذف الكود "${confirmDelete.code}"؟ لا يمكن التراجع عن هذا الإجراء.`}
      </p>

      <div className="flex justify-end gap-3">
        <button
          className="px-4 py-2 bg-gray-200 rounded-lg"
          onClick={() => setConfirmDelete(null)}
        >
          {TEXT.cancel}
        </button>

        <button
          className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
          onClick={async () => {
            try {
  await deleteAdminCoupon(confirmDelete.id);

  setCoupons((prev) =>
    prev.filter((x) => x.id !== confirmDelete.id)
  );

  toast.success(
    lang === "EN"
      ? "Promo code deleted"
      : "تم حذف كود الخصم"
  );

  setConfirmDelete(null);
} catch (err) {
  toast.error(
    lang === "EN"
      ? "This coupon was used in orders and cannot be deleted"
      : "لا يمكن حذف كود خصم تم استخدامه في الطلبات"
  );
}

            setCoupons((prev) =>
              prev.filter((x) => x.id !== confirmDelete.id)
            );

            toast.success(
              lang === "EN"
                ? "Promo code deleted"
                : "تم حذف كود الخصم"
            );

            setConfirmDelete(null);
          }}
        >
          {lang === "EN" ? "Delete" : "حذف"}
        </button>
      </div>
    </div>
  </div>
)}
    </>
  )}


        </main>
      </div>
    </div>
  );
}

export default AdminEmailTemplates;

